import { z } from 'zod'
import type { AvailabilityInput } from '../booking/application/GetListOfAvailabilityBookingUseCase'
import type { POSTBannerResponseType } from '../property/infrastructure/HTTPPropertyRepository/schemas/banner'
import type { POSTRelatedResponseType } from '../property/infrastructure/HTTPPropertyRepository/schemas/related'
import type {
  PropertyPricing,
  SearchProperty,
} from '../property/infrastructure/HTTPPropertyRepository/schemas/v3/search/legacy'
import { Affiliate } from '../shared/domain/Affiliate'
import { Market } from '../shared/domain/Market'
import { Model } from './architecture'
import type { Currencies } from './Currency'
import { Currency, ALLOWED_CURRENCY } from './Currency'
import { Money } from './Money'
import type { KindOfAmount } from './PriceType'
import { PriceType } from './PriceType'

export const PriceValidation = z.object({
  total: z.instanceof(Money),
  totalWithoutDiscount: z.instanceof(Money),
  totalWithFees: z.instanceof(Money),
  payNow: z.instanceof(Money).optional(),
  perNight: z.instanceof(Money),
  perNightWithFees: z.instanceof(Money),
  perNightSubtotal: z.instanceof(Money),
  subtotal: z.instanceof(Money),
  subtotalWithFees: z.instanceof(Money),
  subtotalNightlyWithFees: z.instanceof(Money),
  market: z.instanceof(Market),
  priceType: z.instanceof(PriceType).optional(),
})

export class Price extends Model {
  static create({
    total,
    subtotal,
    totalWithoutDiscount,
    totalWithFees,
    payNow,
    perNight,
    perNightWithFees,
    perNightSubtotal,
    subtotalWithFees,
    subtotalNightlyWithFees,
    market,
    priceType,
  }: z.infer<typeof PriceValidation>) {
    PriceValidation.parse({
      total,
      subtotal,
      totalWithoutDiscount,
      totalWithFees,
      payNow,
      perNight,
      perNightWithFees,
      perNightSubtotal,
      subtotalWithFees,
      subtotalNightlyWithFees,
      market,
      priceType,
    })
    return new Price(
      total,
      totalWithoutDiscount,
      totalWithFees,
      perNight,
      perNightWithFees,
      perNightSubtotal,
      subtotal,
      subtotalWithFees,
      subtotalNightlyWithFees,
      market,
      priceType,
      payNow
    )
  }

  static fromRelatedApi({
    json,
    affiliate,
    market,
  }: {
    json: POSTRelatedResponseType['properties'][number]
    affiliate: Affiliate
    market: Market
  }) {
    let priceWithFees = 0
    let pricePerNightWithFees = 0
    let pricePerNightSubtotal = 0
    let subtotal = 0
    let subtotalWithFees = 0
    let subtotalNightlyWithFees = 0

    json.rooms.forEach(room => {
      const summary = room?.summary
      priceWithFees += summary?.total_with_fees?.amount ?? 0
      pricePerNightWithFees += summary?.total_nightly_with_fees?.amount ?? 0
      subtotal += summary?.subtotal?.amount ?? 0
      subtotalWithFees += summary?.subtotal_with_fees?.amount ?? 0
      subtotalNightlyWithFees += summary?.subtotal_nightly_with_fees?.amount ?? 0
      pricePerNightSubtotal += summary?.subtotal_nightly?.amount ?? 0
    })

    return Price.create({
      total: Money.create({
        amount: json.selling_rate,
        currency: Currency.create({ value: json.currency }),
      }),
      totalWithoutDiscount: Money.create({
        amount: json.bighead_rate,
        currency: Currency.create({ value: json.currency }),
      }),
      perNight: Money.create({
        amount: json.avg_rate,
        currency: Currency.create({ value: json.currency }),
      }),
      totalWithFees: Money.create({
        amount: priceWithFees,
        currency: Currency.create({ value: json.currency }),
      }),
      perNightWithFees: Money.create({
        amount: pricePerNightWithFees,
        currency: Currency.create({ value: json.currency }),
      }),
      perNightSubtotal: Money.create({
        amount: pricePerNightSubtotal,
        currency: Currency.create({ value: json.currency }),
      }),
      subtotal: Money.create({
        amount: subtotal,
        currency: Currency.create({ value: json.currency }),
      }),
      subtotalWithFees: Money.create({
        amount: subtotalWithFees,
        currency: Currency.create({ value: json.currency }),
      }),
      subtotalNightlyWithFees: Money.create({
        amount: subtotalNightlyWithFees,
        currency: Currency.create({ value: json.currency }),
      }),
      market,
      priceType: PriceType.fromAffiliatePriceType({ affiliate, market }),
    })
  }

  static fromBannerApi({
    json,
    affiliate,
    market,
  }: {
    json: POSTBannerResponseType['properties'][number]
    affiliate: Affiliate
    market: Market
  }) {
    let priceWithFees = 0
    let pricePerNightWithFees = 0
    let pricePerNightSubtotal = 0
    let subtotal = 0
    let subtotalWithFees = 0
    let subtotalNightlyWithFees = 0

    json.rooms.forEach(room => {
      const summary = room?.summary
      priceWithFees += summary?.total_with_fees?.amount ?? 0
      pricePerNightWithFees += summary?.total_nightly_with_fees?.amount ?? 0
      subtotal += summary?.subtotal?.amount ?? 0
      subtotalWithFees += summary?.subtotal_with_fees?.amount ?? 0
      subtotalNightlyWithFees += summary?.subtotal_nightly_with_fees?.amount ?? 0
      pricePerNightSubtotal += summary?.subtotal_nightly?.amount ?? 0
    })

    return Price.create({
      total: Money.create({
        amount: json.selling_rate,
        currency: Currency.create({ value: json.currency }),
      }),
      totalWithoutDiscount: Money.create({
        amount: json.bighead_rate,
        currency: Currency.create({ value: json.currency }),
      }),
      perNight: Money.create({
        amount: json.avg_rate,
        currency: Currency.create({ value: json.currency }),
      }),
      totalWithFees: Money.create({
        amount: priceWithFees,
        currency: Currency.create({ value: json.currency }),
      }),
      perNightWithFees: Money.create({
        amount: pricePerNightWithFees,
        currency: Currency.create({ value: json.currency }),
      }),
      perNightSubtotal: Money.create({
        amount: pricePerNightSubtotal,
        currency: Currency.create({ value: json.currency }),
      }),
      subtotal: Money.create({
        amount: subtotal,
        currency: Currency.create({ value: json.currency }),
      }),
      subtotalWithFees: Money.create({
        amount: subtotalWithFees,
        currency: Currency.create({ value: json.currency }),
      }),
      subtotalNightlyWithFees: Money.create({
        amount: subtotalNightlyWithFees,
        currency: Currency.create({ value: json.currency }),
      }),
      market,
      priceType: PriceType.fromAffiliatePriceType({ affiliate, market }),
    })
  }

  static fromBookingPurchaseApi({
    json,
    affiliate,
    market,
  }: {
    json: AvailabilityInput
    affiliate: Affiliate
    market: Market
  }) {
    const pricePerNightSubtotal = json.summary?.subtotal_nightly?.amount ?? 0
    const pricePerNightWithFees = json.summary?.total_nightly_with_fees?.amount ?? 0
    const priceWithFees = json.summary?.total_with_fees?.amount ?? 0
    const subtotal = json.summary?.subtotal?.amount ?? 0
    const subtotalNightlyWithFees = json.summary?.subtotal_nightly_with_fees?.amount ?? 0
    const subtotalWithFees = json.summary?.subtotal_with_fees?.amount ?? 0

    return Price.create({
      ...(json.pay_now && {
        payNow: Money.create({
          amount: json.pay_now.amount,
          currency: Currency.create({ value: json.pay_now.currency }),
        }),
      }),
      perNight: Money.create({
        amount: json.avg_rate,
        currency: Currency.create({ value: json.currency }),
      }),
      perNightWithFees: Money.create({
        amount: pricePerNightWithFees,
        currency: Currency.create({ value: json.currency }),
      }),
      totalWithFees: Money.create({
        amount: priceWithFees,
        currency: Currency.create({ value: json.currency }),
      }),
      perNightSubtotal: Money.create({
        amount: pricePerNightSubtotal,
        currency: Currency.create({ value: json.currency }),
      }),
      subtotal: Money.create({
        amount: subtotal,
        currency: Currency.create({ value: json.currency }),
      }),
      subtotalNightlyWithFees: Money.create({
        amount: subtotalNightlyWithFees,
        currency: Currency.create({ value: json.currency }),
      }),
      subtotalWithFees: Money.create({
        amount: subtotalWithFees,
        currency: Currency.create({ value: json.currency }),
      }),
      total: Money.create({
        amount: json.selling_rate,
        currency: Currency.create({ value: json.currency }),
      }),
      totalWithoutDiscount: Money.create({
        amount: json.bighead_rate,
        currency: Currency.create({ value: json.currency }),
      }),
      market,
      priceType: PriceType.fromAffiliatePriceType({ affiliate, market }),
    })
  }

  static fromSerpApiLegacyOrUndefined({
    json,
    market,
    affiliate,
  }: {
    json: SearchProperty
    market?: Market
    affiliate?: Affiliate
  }): Price | undefined {
    if (!market) {
      return undefined
    }

    const jsonWithPrices = json as SearchProperty & PropertyPricing

    const {
      price: totalPrice = 0,
      priceOld = 0,
      pricePerNight = 0,
      pricePerNightWithFees = 0,
      pricePerNightSubtotal = 0,
      priceWithFees = 0,
      subtotal = 0,
      subtotalNightlyWithFees = 0,
      subtotalWithFees = 0,
    } = jsonWithPrices

    if (!totalPrice || totalPrice <= 0) {
      return undefined
    }

    const isAllowedCurrency = (c?: string): c is Currencies =>
      Boolean(c && (ALLOWED_CURRENCY as ReadonlyArray<string>).includes(c))

    const candidateCurrency: string | undefined = json.currency

    const currency = isAllowedCurrency(candidateCurrency)
      ? Currency.create({ value: candidateCurrency })
      : Currency.fromMarket({ market })
    const defaultAffiliate = affiliate || Affiliate.default()
    const priceType = PriceType.fromAffiliatePriceType({
      affiliate: defaultAffiliate,
      market,
    })

    return Price.create({
      total: Money.create({ amount: totalPrice, currency }),
      subtotal: Money.create({ amount: subtotal, currency }),
      totalWithoutDiscount: Money.create({ amount: priceOld, currency }),
      totalWithFees: Money.create({ amount: priceWithFees, currency }),
      perNight: Money.create({ amount: pricePerNight, currency }),
      perNightWithFees: Money.create({ amount: pricePerNightWithFees, currency }),
      perNightSubtotal: Money.create({ amount: pricePerNightSubtotal, currency }),
      subtotalWithFees: Money.create({ amount: subtotalWithFees, currency }),
      subtotalNightlyWithFees: Money.create({ amount: subtotalNightlyWithFees, currency }),
      market,
      priceType,
      payNow: undefined,
    })
  }

  static fromJSON(json: ReturnType<Price['toJSON']>) {
    return new Price(
      Money.create({
        amount: json.total.amount,
        currency: Currency.create({ value: json.total.currency }),
      }),
      Money.create({
        amount: json.totalWithoutDiscount.amount,
        currency: Currency.create({ value: json.totalWithoutDiscount.currency }),
      }),
      Money.create({
        amount: json.totalWithFees.amount,
        currency: Currency.create({ value: json.totalWithFees.currency }),
      }),
      Money.create({
        amount: json.perNight.amount,
        currency: Currency.create({ value: json.perNight.currency }),
      }),
      Money.create({
        amount: json.perNightWithFees.amount,
        currency: Currency.create({ value: json.perNightWithFees.currency }),
      }),
      Money.create({
        amount: json.perNightSubtotal.amount,
        currency: Currency.create({ value: json.perNightSubtotal.currency }),
      }),
      Money.create({
        amount: json.subtotal.amount,
        currency: Currency.create({ value: json.subtotal.currency }),
      }),
      Money.create({
        amount: json.subtotalWithFees.amount,
        currency: Currency.create({ value: json.subtotalWithFees.currency }),
      }),
      Money.create({
        amount: json.subtotalNightlyWithFees.amount,
        currency: Currency.create({ value: json.subtotalNightlyWithFees.currency }),
      }),
      Market.fromJSON(json.market),
      PriceType.fromJSON(json.priceType),
      Money.createOrUndefined({
        amount: json.payNow?.amount,
        currency: Currency.createOrUndefined({ value: json.payNow?.currency }),
      })
    )
  }

  constructor(
    private readonly _total: Money,
    private readonly _totalWithoutDiscount: Money,
    private readonly _totalWithFees: Money,
    private readonly _perNight: Money,
    private readonly _perNightWithFees: Money,
    private readonly _perNightSubtotal: Money,
    private readonly _subtotal: Money,
    private readonly _subtotalWithFees: Money,
    private readonly _subtotalNightlyWithFees: Money,
    private readonly _market: Market,
    private readonly _priceType: PriceType = PriceType.create({ value: 'gross' }),
    private readonly _payNow?: Money
  ) {
    super()
  }

  get total() { return this._total } // prettier-ignore
  get totalWithoutDiscount() { return this._totalWithoutDiscount } // prettier-ignore
  get totalWithFees() { return this._totalWithFees } // prettier-ignore
  get payNow() { return this._payNow } // prettier-ignore
  get perNight() { return this._perNight } // prettier-ignore
  get perNightWithFees() { return this._perNightWithFees } // prettier-ignore
  get perNightSubtotal() { return this._perNightSubtotal } // prettier-ignore
  get subtotal() { return this._subtotal } // prettier-ignore
  get subtotalWithFees() { return this._subtotalWithFees } // prettier-ignore
  get subtotalNightlyWithFees() { return this._subtotalNightlyWithFees } // prettier-ignore
  get priceType() { return this._priceType } // prettier-ignore
  get market() { return this._market } // prettier-ignore

  getMoney(origin: KindOfAmount | 'priceWithoutDiscount' | 'payNow') {
    const price = this.#getPriceNormalizedByType()

    if (origin === 'priceWithoutDiscount') {
      return this.totalWithoutDiscount
    }

    if (origin === 'payNow') {
      return this.payNow
    }

    return price[origin]
  }

  getFeaturedPrice() {
    return this.priceType.getFeaturedPrice()
  }

  getFormatted(locale: string, origin: KindOfAmount | 'priceWithoutDiscount' | 'payNow') {
    return this.getMoney(origin)?.toFormattedString(locale, this.market)
  }

  hasVatsIncluded() {
    return this.priceType.hasVats()
  }

  hasTaxIncluded() {
    return this.priceType.hasTax()
  }

  hasFeesIncluded() {
    return this.priceType.hasFees()
  }

  hasDiscount() {
    return this.totalWithoutDiscount.amount > this.#getPriceNormalizedByType().total.amount
  }

  toJSON() {
    return {
      total: this.total.toJSON(),
      totalWithoutDiscount: this.totalWithoutDiscount.toJSON(),
      perNight: this.perNight.toJSON(),
      totalWithFees: this.totalWithFees.toJSON(),
      perNightWithFees: this.perNightWithFees.toJSON(),
      perNightSubtotal: this.perNightSubtotal.toJSON(),
      subtotal: this.subtotal.toJSON(),
      subtotalWithFees: this.subtotalWithFees.toJSON(),
      subtotalNightlyWithFees: this.subtotalNightlyWithFees.toJSON(),
      market: this.market.toJSON(),
      priceType: this.priceType.toJSON(),
      ...(this.payNow && { payNow: this.payNow.toJSON() }),
    }
  }

  isFeaturedPrice(origin: KindOfAmount) {
    return this.getFeaturedPrice() === origin
  }

  #getPriceNormalizedByType() {
    const featuredPrice = {
      net: {
        perNight: this.perNightSubtotal,
        total: this.total,
      },
      netSubtotal: {
        perNight: this.perNightSubtotal,
        total: this.subtotal,
      },
      gross: {
        perNight: this.perNight,
        total: this.total,
      },
      allIn: {
        perNight: this.perNightWithFees,
        total: this.totalWithFees,
      },
      allInDaily: {
        perNight: this.perNightWithFees,
        total: this.totalWithFees,
      },
      netWithFees: {
        perNight: this.subtotalWithFees,
        total: this.subtotalNightlyWithFees,
      },
      netWithFeesDaily: {
        perNight: this.subtotalNightlyWithFees,
        total: this.subtotalWithFees,
      },
    }

    return featuredPrice[this.priceType.value]
  }

  percentageDiscount() {
    const price = this.#getPriceNormalizedByType().total.amount
    const priceBefore = this.totalWithoutDiscount.amount

    return price && priceBefore && price < priceBefore
      ? Math.ceil(100 - (price / priceBefore) * 100) * -1
      : null
  }
}
